import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowRight, Building2, CalendarDays,
  ChevronDown, ChevronLeft, ChevronRight, CircleDot, Clock, DollarSign,
  GitBranch, HeartPulse, Layers3, LayoutDashboard, ListChecks, Loader2, Network, PanelLeftClose,
  RefreshCw, Search, ShieldCheck, Stethoscope, Users, X,
} from 'lucide-react';
import { emergeApi } from './emergeApi';
import type {
  DashboardSummary, FacilityDetail, FacilitySummary, ReadinessFacility, ReadinessSummary, SyncStatusRow,
} from './emergeApi';
import DataTrustPage from './dataTrust/DataTrustPage';
import PlatformBlueprintPage from './platformBlueprint/PlatformBlueprintPage';
import type { UserRole } from './types';
import { instant, MONDAY_RETIRED_ON } from '../../components/workspaceFreshness';
import './emerge.css';
import './emerge-overrides.css';

type Page = 'command' | 'organizations' | 'readiness' | 'dataSync' | 'dataTrust' | 'blueprint';

const pageRoutes: Record<Page, string> = {
  command: '/portal/',
  organizations: '/portal/organizations',
  readiness: '/portal/readiness',
  dataSync: '/portal/data-sync',
  dataTrust: '/portal/data-trust',
  blueprint: '/portal/blueprint',
};

export function pageFromPath(pathname: string): Page {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  const match = (Object.entries(pageRoutes) as [Page, string][]).find(([, route]) => route.replace(/\/+$/, '') === normalized);
  return match?.[0] ?? 'command';
}

const roles: UserRole[] = ['Executive', 'Strategy & Informatics', 'Growth / Sales', 'Operations', 'Engineering'];

const pageMeta: Record<Page, { label: string; icon: typeof LayoutDashboard }> = {
  command: { label: 'Command Center', icon: LayoutDashboard },
  organizations: { label: 'Organizations', icon: Building2 },
  readiness: { label: 'Launch Readiness', icon: ListChecks },
  dataSync: { label: 'Data Sync Status', icon: Network },
  dataTrust: { label: 'Data Trust & Context', icon: GitBranch },
  blueprint: { label: 'Platform Blueprint', icon: Layers3 },
};

function StatusChip({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'good' | 'warn' | 'danger' | 'info' | 'neutral' }) {
  return <span className={`em-chip ${tone}`}>{children}</span>;
}

function PageHeader({ eyebrow, title, summary, action }: { eyebrow: string; title: string; summary: string; action?: React.ReactNode }) {
  return <header className="em-page-header"><div><div className="em-eyebrow">{eyebrow}</div><h1>{title}</h1><p>{summary}</p></div>{action}</header>;
}

function Metric({ label, value, delta, icon: Icon }: { label: string; value: string; delta: string; icon: typeof Building2 }) {
  return <article className="em-metric"><div className="em-metric-icon"><Icon size={18} /></div><div><span>{label}</span><strong>{value}</strong><small>{delta}</small></div></article>;
}

function ScoreRing({ value, label }: { value: number; label: string }) {
  return <div className="em-ring" style={{ '--score': `${value * 3.6}deg` } as React.CSSProperties}><div><strong>{value}</strong><span>{label}</span></div></div>;
}

function LoadingBlock() {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '40px 0', color: 'var(--muted)', fontSize: 12 }}><Loader2 size={16} className="animate-spin" /> Loading real data&hellip;</div>;
}

const fmtCurrency = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
const fmtRelative = (d: string | null) => {
  if (!d) return 'never';
  const ms = Date.now() - new Date(d).getTime();
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;

};

/* ── Command Center ─────────────────────────────────────────────── */
function CommandCenter({ go }: { go: (p: Page) => void }) {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [readiness, setReadiness] = useState<ReadinessSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [summaryRes, readinessRes] = await Promise.all([emergeApi.getSummary(), emergeApi.getReadiness()]);
    if (summaryRes.success && summaryRes.data) setSummary(summaryRes.data);
    if (readinessRes.success && readinessRes.data) setReadiness(readinessRes.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !summary) {
    return <><PageHeader eyebrow="Executive intelligence" title="Executive Command Center" summary="One view of facility growth, launch readiness, and the decisions that need attention." /><LoadingBlock /></>;
  }

  const attentionItems = readiness ? [
    ...readiness.needsAttention.missingBaa.slice(0, 2).map((f) => ({ f, label: 'BAA not signed' })),
    ...readiness.needsAttention.noCredentialing.slice(0, 1).map((f) => ({ f, label: 'Credentialing incomplete' })),
  ].slice(0, 3) : [];

  const awaitingFirstVisit = readiness?.needsAttention.noVisit ?? [];

  return <>
    <PageHeader eyebrow="Executive intelligence" title="Executive Command Center" summary="One view of facility growth, launch readiness, and the decisions that need attention." action={<button className="em-btn ghost" onClick={load}><RefreshCw size={16} /> Refresh</button>} />
    <div className="em-data-note"><CircleDot size={13} /> Live from the eMerge database <span>&middot;</span> {summary.facilities.total.toLocaleString()} facilities tracked</div>
    <section className="em-metrics">
      <Metric label="Active facilities" value={summary.facilities.active.toLocaleString()} delta={`${summary.facilities.total.toLocaleString()} total facilities`} icon={Building2} />
      <Metric label="Credentialed" value={summary.facilities.credentialed.toLocaleString()} delta={`${summary.facilities.baa_signed.toLocaleString()} BAAs signed`} icon={ShieldCheck} />
      <Metric label="Patients under care" value={summary.patients.active.toLocaleString()} delta={`${summary.patients.total.toLocaleString()} total patients`} icon={Users} />
      <Metric label="Upcoming visits" value={summary.scheduling.upcomingVisits.toLocaleString()} delta={`${summary.scheduling.last_30d} completed last 30 days`} icon={CalendarDays} />
      <Metric label="Monday mirror (historical): outstanding" value={fmtCurrency(summary.finance.remaining_amount)} delta={`${summary.finance.historical ? summary.finance.historical.label : 'Monday historical mirror, date not recorded'} · not the live balance · ${summary.finance.past_due_accounts} accounts past due`} icon={DollarSign} />
      <Metric label="Hearing aids delivered" value={summary.hearingAids.delivered.toLocaleString()} delta={`${summary.hearingAids.total.toLocaleString()} total records`} icon={Stethoscope} />
    </section>
    <section className="em-dashboard-grid">
      <article className="em-card em-funnel-card">
        <div className="em-card-head"><div><span>Facility mix</span><h2>SNF vs. ALF</h2></div><StatusChip tone="good">{summary.facilities.total.toLocaleString()} total</StatusChip></div>
        <div className="em-funnel">
          <div className="em-funnel-row"><span>SNF</span><div><i style={{ width: `${(summary.facilities.snf / summary.facilities.total) * 100}%` }} /></div><strong>{summary.facilities.snf}</strong><small>Skilled Nursing</small></div>
          <div className="em-funnel-row"><span>ALF</span><div><i style={{ width: `${(summary.facilities.alf / summary.facilities.total) * 100}%` }} /></div><strong>{summary.facilities.alf}</strong><small>Assisted Living</small></div>
        </div>
        <button className="em-text-btn" onClick={() => go('organizations')}>Browse all facilities <ArrowRight size={14} /></button>
      </article>
      <article className="em-card em-attention">
        <div className="em-card-head"><div><span>What needs attention</span><h2>{attentionItems.length} onboarding gaps</h2></div><AlertTriangle size={20} /></div>
        {attentionItems.length === 0 && <p style={{ fontSize: 12, color: 'var(--muted)', padding: '13px 0' }}>No open onboarding gaps among active facilities.</p>}
        {attentionItems.map(({ f, label }) => (
          <button key={f.id} onClick={() => go('readiness')}><b className="danger-dot" /><div><strong>{f.name}</strong><span>{label} &middot; {f.city || 'Unknown city'}</span></div><ChevronRight size={17} /></button>
        ))}
      </article>
      <article className="em-card em-launches">
        <div className="em-card-head"><div><span>Awaiting first visit</span><h2>Facilities with no scheduled visit</h2></div><StatusChip tone="info">{awaitingFirstVisit.length} shown</StatusChip></div>
        {awaitingFirstVisit.slice(0, 3).map((f) => <div className="em-launch" key={f.id}><CalendarDays size={17} /><div><strong>{f.name}</strong><span>{f.city || 'Unknown city'} &middot; readiness {f.readiness_score}%</span></div><StatusChip tone={f.readiness_score >= 60 ? 'warn' : 'danger'}>{f.readiness_score}%</StatusChip></div>)}
        {awaitingFirstVisit.length === 0 && <p style={{ fontSize: 12, color: 'var(--muted)', padding: '13px 0' }}>Every active facility has had a visit.</p>}
      </article>
      <article className="em-card em-specialty">
        <div className="em-card-head"><div><span>Hearing aid types</span><h2>Delivered &amp; in progress</h2></div><span className="em-muted">{summary.hearingAids.total.toLocaleString()} total records</span></div>
        {summary.hearingAids.byType.map((t) => <div className="em-bar" key={t.hearing_aid_type}><span>{t.hearing_aid_type}</span><div><i style={{ width: `${(t.count / summary.hearingAids.total) * 100}%` }} /></div><strong>{t.count.toLocaleString()}</strong></div>)}
      </article>
    </section>
  </>;
}

/* ── Organizations (real facility directory + drill-down) ──────────── */
function Organizations() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rows, setRows] = useState<FacilitySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [detail, setDetail] = useState<FacilityDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await emergeApi.getFacilities({ page, pageSize: 20, q: query || undefined });
    if (res.success && res.data) setRows(res.data);
    if (res.pagination) setTotal(res.pagination.total);
    setLoading(false);
  }, [page, query]);

  useEffect(() => { load(); }, [load]);

  const openFacility = async (id: string) => {
    setSelectedId(id);
    setDetailLoading(true);
    const res = await emergeApi.getFacility(id);
    if (res.success && res.data) setDetail(res.data);
    setDetailLoading(false);
  };

  const totalPages = Math.max(1, Math.ceil(total / 20));

  if (selectedId) {
    return <>
      <div className="em-breadcrumb"><button onClick={() => { setSelectedId(null); setDetail(null); }}>Organizations</button><ChevronRight size={13} /> {detail?.name ?? 'Facility'}</div>
      {detailLoading || !detail ? <LoadingBlock /> : <>
        <section className="em-account-hero">
          <div className="em-logo-mark">{detail.facility_type ?? '—'}</div>
          <div className="em-account-title">
            <div className="em-eyebrow">{detail.facility_type_name ?? 'Facility'} &middot; {detail.region || 'Region unassigned'}</div>
            <h1>{detail.name}</h1>
            <p>{[detail.address, detail.city, detail.state, detail.zip_code].filter(Boolean).join(', ') || 'Address on file with Monday.com'}</p>
            <div>
              <StatusChip tone={detail.status === 'Active' ? 'good' : 'neutral'}>{detail.status || 'Status unknown'}</StatusChip>
              {detail.baa_signed && <StatusChip tone="info">BAA signed</StatusChip>}
              {detail.credentialing_status && <StatusChip>{detail.credentialing_status}</StatusChip>}
            </div>
          </div>
        </section>
        <section className="em-two-col" style={{ marginTop: 15 }}>
          <div className="em-stack">
            <article className="em-card">
              <div className="em-card-head"><div><span>Facility snapshot</span><h2>Operational summary</h2></div></div>
              <div className="em-facts">
                <div><span>Licensed beds</span><strong>{detail.licensed_beds || '—'}</strong></div>
                <div><span>Patients under care</span><strong>{detail.patients.active} / {detail.patients.total}</strong></div>
                <div><span>Upcoming visits</span><strong>{detail.scheduling.upcoming}</strong></div>
                <div><span>Last visit</span><strong>{fmtDate(detail.last_visit_date)}</strong></div>
              </div>
            </article>
            <article className="em-card">
              <div className="em-card-head"><div><span>Monday mirror (historical)</span><h2>Account balances</h2></div></div>
              <p className="em-data-note" role="note">{detail.finance.historical ? detail.finance.historical.warning : 'Monday historical mirror: when it was last synced is not recorded. This is not the live balance; the eMerge ledger is.'}</p>
              <div className="em-facts compact">
                <div><span>Total billed</span><strong>{fmtCurrency(detail.finance.total_sale_value)}</strong></div>
                <div><span>Remaining</span><strong>{fmtCurrency(detail.finance.remaining_amount)}</strong></div>
                <div><span>Overdue</span><strong>{fmtCurrency(detail.finance.amount_overdue)}</strong></div>
              </div>
            </article>
          </div>
          <aside className="em-stack">
            <article className="em-card">
              <div className="em-card-head"><div><span>Contact</span><h2>Facility details</h2></div></div>
              <div className="em-detail-list">
                <span><b>Phone</b>{detail.phone || '—'}</span>
                <span><b>Email</b>{detail.email || '—'}</span>
                <span><b>BAA sent</b>{fmtDate(detail.baa_sent_date)}</span>
                <span><b>BAA signed</b>{fmtDate(detail.baa_signed_date)}</span>
                <span><b>Next TM follow-up</b>{fmtDate(detail.tm_next_follow_up_date)}</span>
              </div>
            </article>
          </aside>
        </section>
      </>}
    </>;
  }

  return <>
    <PageHeader eyebrow="Real facility directory" title="Organizations" summary="Every SNF/ALF facility under management, sourced live from the eMerge database." action={<div className="em-search"><Search size={16} /><input aria-label="Search facilities" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} placeholder="Search by name or city" /></div>} />
    <section className="em-card em-table-card">
      <table>
        <thead><tr><th>Facility</th><th>City</th><th>Type</th><th>Status</th><th>Licensed beds</th><th>BAA</th></tr></thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.id} onClick={() => openFacility(f.id)}>
              <td><strong>{f.name}</strong></td>
              <td>{f.city || '—'}</td>
              <td>{f.facility_type || '—'}</td>
              <td><StatusChip tone={f.status === 'Active' ? 'good' : 'neutral'}>{f.status || 'Unknown'}</StatusChip></td>
              <td>{f.licensed_beds || '—'}</td>
              <td>{f.baa_signed ? <StatusChip tone="good">Signed</StatusChip> : <StatusChip tone="warn">Pending</StatusChip>}</td>
            </tr>
          ))}
          {!loading && rows.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--muted)' }}>No facilities found</td></tr>}
        </tbody>
      </table>
    </section>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, fontSize: 11, color: 'var(--muted)' }}>
      <span>{total.toLocaleString()} facilities &middot; page {page} of {totalPages}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="em-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft size={14} /></button>
        <button className="em-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}><ChevronRight size={14} /></button>
      </div>
    </div>
  </>;
}

/* ── Launch Readiness (real, from facilities BAA / credentialing_status / last_visit_date) ── */
function Readiness({ go }: { go: (p: Page) => void }) {
  const [data, setData] = useState<ReadinessSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'missingBaa' | 'noCredentialing' | 'noVisit'>('missingBaa');

  useEffect(() => {
    emergeApi.getReadiness().then((res) => {
      if (res.success && res.data) setData(res.data);
      setLoading(false);
    });
  }, []);

  if (loading || !data) {
    return <><PageHeader eyebrow="Launch readiness" title="Facility Onboarding Readiness" summary="Computed from real BAA, credentialing, and first-visit data." /><LoadingBlock /></>;
  }

  const rows: ReadinessFacility[] = data.needsAttention[filter];
  const filterLabels: Record<typeof filter, string> = {
    missingBaa: 'Missing BAA',
    noCredentialing: 'Not credentialed',
    noVisit: 'No visit yet',
  };

  return <>
    <PageHeader eyebrow="Launch readiness" title="Facility Onboarding Readiness" summary={`Computed across ${data.facilityCount.toLocaleString()} active facilities from real BAA, credentialing, and visit data.`} action={<button className="em-btn ghost" onClick={() => go('organizations')}>Browse organizations <ArrowRight size={14} /></button>} />
    <section className="em-readiness-hero">
      <ScoreRing value={data.averageScore} label="average readiness" />
      <div className="em-category-scores">
        {data.categories.map((c) => <div key={c.category}><div><span>{c.category}</span><strong>{c.score}%</strong></div><div><i style={{ width: `${c.score}%` }} /></div></div>)}
      </div>
      <div className="em-blocker-summary">
        <span>Onboarding gate</span>
        <strong><AlertTriangle size={20} /> {data.needsAttention.missingBaa.length + data.needsAttention.noCredentialing.length} facilities need action</strong>
        <p>BAA execution and credentialing are prerequisites before a facility is fully onboarded.</p>
      </div>
    </section>
    <section className="em-card em-workstreams">
      <div className="em-card-head">
        <div><span>Facilities needing action</span><h2>{filterLabels[filter]}</h2></div>
        <div className="em-filter-group">
          {(['missingBaa', 'noCredentialing', 'noVisit'] as const).map((f) => <button key={f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>{filterLabels[f]}</button>)}
        </div>
      </div>
      <div className="em-table-scroll">
        <table>
          <thead><tr><th>Facility</th><th>City / State</th><th>BAA</th><th>Credentialing</th><th>Last visit</th></tr></thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.id}>
                <td><strong>{f.name}</strong></td>
                <td>{[f.city, f.state].filter(Boolean).join(', ') || '—'}</td>
                <td>{f.baa_signed ? <StatusChip tone="good">Signed</StatusChip> : f.baa_sent ? <StatusChip tone="warn">Sent</StatusChip> : <StatusChip tone="danger">Not sent</StatusChip>}</td>
                <td>{f.credentialing_status || <StatusChip tone="warn">Pending</StatusChip>}</td>
                <td>{fmtDate(f.last_visit_date)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>No facilities in this category</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  </>;
}

/* ── Data Sync Status (real, from sync_xref) ────────────────────────── */
/*
 * 2026-10-12, after the Monday sunset (components/workspaceFreshness.ts,
 * MONDAY_RETIRED_ON): the sync is retired for good, so every table's last
 * sync is FINAL, not stale. The page says so neutrally instead of turning
 * every row red because a source that was switched off stopped writing.
 */
const RETIRED_ON_LABEL = new Date(`${MONDAY_RETIRED_ON}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });

export function DataSyncStatus() {
  const [rows, setRows] = useState<SyncStatusRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await emergeApi.getSyncStatus();
    if (res.success && res.data) setRows(res.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalRows = rows.reduce((sum, r) => sum + r.row_count, 0);
  const finalSync = rows.reduce<string | null>((latest, r) => (!latest || new Date(r.last_synced_at) > new Date(latest) ? r.last_synced_at : latest), null);

  return <>
    <PageHeader eyebrow="Monday.com integration · retired" title="Data Sync Status" summary={`The Monday board was retired on ${RETIRED_ON_LABEL} and its sync no longer runs. These are the final sync times of every table it fed; eMerge works from its own data now.`} action={<button className="em-btn ghost" onClick={load}><RefreshCw size={16} /> Refresh</button>} />
    <div className="em-inline-alert"><ShieldCheck size={18} /><div><strong>Monday board retired</strong><span>{finalSync ? `Its last data is from ${instant(finalSync)}. ` : ''}A table here is final, not stale: the source was switched off, nothing failed.</span></div></div>
    {loading ? <LoadingBlock /> : <>
      <section className="em-integration-stats">
        <div><strong>{rows.length}</strong><span>Tables tracked</span></div>
        <div><strong>{totalRows.toLocaleString()}</strong><span>Rows synced</span></div>
        <div><strong>{instant(finalSync) || '—'}</strong><span>Final sync</span></div>
        <div><strong>Retired</strong><span>Sync frequency</span></div>
      </section>
      <section className="em-card em-table-card">
        <table>
          <thead><tr><th>Table</th><th>Rows</th><th>Last synced</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.target_table}>
                <td><code>{r.target_table}</code></td>
                <td>{r.row_count.toLocaleString()}</td>
                <td title={fmtRelative(r.last_synced_at)}><Clock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{instant(r.last_synced_at) || 'never'}</td>
                <td><StatusChip tone="neutral">Final</StatusChip></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>}
  </>;
}

/* ── Shell ───────────────────────────────────────────────────────── */
export default function EMergeDemo() {
  const [page, setPage] = useState<Page>(() => pageFromPath(window.location.pathname));
  const [role, setRole] = useState<UserRole>('Executive');
  const [navOpen, setNavOpen] = useState(() => window.innerWidth > 900);

  const roleNote = useMemo(() => ({
    Executive: 'Facility growth, launch readiness & risk',
    'Strategy & Informatics': 'Data quality, governance & readiness',
    'Growth / Sales': 'Facility partnerships & outreach',
    Operations: 'Launches, scheduling & capacity',
    Engineering: 'Data sync health & platform architecture',
  }[role]), [role]);

  useEffect(() => {
    const onPopState = () => setPage(pageFromPath(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const go = (next: Page) => {
    setPage(next);
    if (window.innerWidth <= 800) setNavOpen(false);
    if (window.location.pathname !== pageRoutes[next]) window.history.pushState({}, '', pageRoutes[next]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return <div className={`em-app ${navOpen ? '' : 'nav-closed'}`}>
    <aside className="em-nav" id="em-primary-navigation">
      <button className="em-nav-mobile-close" onClick={() => setNavOpen(false)} aria-label="Close navigation"><X /></button>
      <div className="em-brand"><div className="em-brand-mark"><HeartPulse /></div><div><strong>eMerge</strong><span>Facility Growth &amp; Care Operations</span></div></div>
      <nav>{(Object.keys(pageMeta) as Page[]).map((p) => { const Icon = pageMeta[p].icon; return <button key={p} title={pageMeta[p].label} className={page === p ? 'active' : ''} onClick={() => go(p)}><Icon /><span>{pageMeta[p].label}</span></button>; })}</nav>
      <div className="em-nav-footer"><ShieldCheck /><div><strong>Live operational data</strong><span>Facility &amp; patient records via Monday.com sync</span></div></div>
    </aside>
    <header className="em-topbar">
      <button className="em-nav-toggle" onClick={() => setNavOpen(!navOpen)} aria-label="Toggle navigation" aria-controls="em-primary-navigation" aria-expanded={navOpen}><PanelLeftClose /></button>
      <div className="em-context"><span>eMerge Platform</span><i>/</i><strong>{pageMeta[page].label}</strong></div>
      <div className="em-role">
        <div><span>Viewing as</span><strong>{roleNote}</strong></div>
        <label><select aria-label="Demo role" value={role} onChange={(e) => setRole(e.target.value as UserRole)}>{roles.map((r) => <option key={r}>{r}</option>)}</select><ChevronDown /></label>
      </div>
      <div className="em-avatar">{role === 'Strategy & Informatics' ? 'SI' : role.split(' ').map((x) => x[0]).join('').slice(0, 2)}</div>
    </header>
    <main>
      {page === 'command' && <CommandCenter go={go} />}
      {page === 'organizations' && <Organizations />}
      {page === 'readiness' && <Readiness go={go} />}
      {page === 'dataSync' && <DataSyncStatus />}
      {page === 'dataTrust' && <DataTrustPage activeRole={role} />}
      {page === 'blueprint' && <PlatformBlueprintPage />}
    </main>
    <footer className="em-disclaimer"><span>eMerge Operations</span><i>&middot;</i><span>Internal Use Only</span><i>&middot;</i><span>Not for Clinical Use</span></footer>
  </div>;
}
